import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Shield, Eye } from "lucide-react";
import {
  WELLNESS_OPTIONS,
  useWellnessSettings,
  setWellnessSettings,
} from "@/lib/wellnessSettings";

/**
 * Admin chip that opens a small Popover to control the Wellness reminder.
 * Toggle ON/OFF, choose the idle threshold.
 */
export function AdminMenuChip() {
  const { enabled, thresholdMin } = useWellnessSettings();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hidden sm:inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-medium hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
          data-testid="admin-menu-chip"
          aria-label="Admin menu"
        >
          <Shield className="h-3 w-3" />
          Admin
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-72 p-0 overflow-hidden bg-white shadow-xl"
        data-testid="admin-menu-popover"
      >
        <div className="border-b bg-amber-50/60 px-3 py-2.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-amber-900">
            <Eye className="h-3.5 w-3.5" />
            Wellness reminder
          </div>
          <div className="mt-0.5 text-[11px] text-amber-900/70">
            A playful nudge to rest your eyes when idle.
          </div>
        </div>

        <div className="space-y-3 px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="wellness-toggle" className="text-[12px] font-medium">
              Enable reminder
            </Label>
            <Switch
              id="wellness-toggle"
              checked={enabled}
              onCheckedChange={(v) => setWellnessSettings({ enabled: !!v })}
              data-testid="wellness-toggle"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[12px] font-medium text-slate-700">
              Show after idle for
            </Label>
            <Select
              value={String(thresholdMin)}
              onValueChange={(v) => setWellnessSettings({ thresholdMin: parseInt(v, 10) })}
              disabled={!enabled}
            >
              <SelectTrigger
                className="h-8 text-[12px]"
                data-testid="wellness-threshold-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WELLNESS_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={String(o.value)}
                    className="text-[12px]"
                    data-testid={`wellness-threshold-${o.value}`}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-[11px] leading-relaxed text-muted-foreground">
            When idle (no mouse, keyboard, scroll or touch) for the chosen
            duration, a friendly modal appears suggesting a short break.
            Closing the modal resumes the timer.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AdminMenuChip;
